import { useDeleteExportConfig, useGetExportConfigs } from "@/api/export";
import StyledDrawer from "@/components/StyledDrawer";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ExportConfig, ExportConfigDto } from "@/lib/auth/export/export-types";
import { TabMenuItems } from "@/lib/types";
import { tabMenuKeyToTranslationKey } from "@/lib/utils";
import { Save, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type SaveExportProps = {
  handleSave: (exportName: string) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
};

export const SaveExport = ({ handleSave, open, setOpen }: SaveExportProps) => {
  const { t } = useTranslation();
  const [exportName, setExportName] = useState("");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex-1 gap-1">
          <Save className="w-4 h-4" />
          <span className="text-sm font-medium">{t("export.save")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent needCloseButton={false} className="px-0">
        <DialogHeader className="border-b border-gray-300 pb-4 px-6">
          <DialogTitle className="text-lg font-semibold">
            {t("export.save.title")}
          </DialogTitle>
          <DialogDescription className="hidden">
            {t("export.save.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 px-8 py-4">
          <p className="text-sm font-medium text-gray-700">
            {t("export.save.name")}
          </p>
          <Input
            value={exportName}
            onChange={(e) => setExportName(e.target.value)}
            placeholder={t("export.save.namePlaceholder")}
          />
        </div>
        <DialogFooter
          className="border-t border-gray-300 px-4 pt-4 gap-2"
          style={{
            boxShadow: "0 -4px 8px -2px rgba(16, 24, 40, 0.10)",
          }}
        >
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => setOpen(false)}
          >
            <span className="font-semibold">{t("cancel")}</span>
          </Button>
          <Button className="flex-1" onClick={() => handleSave(exportName)}>
            <span className="font-semibold">{t("saveButton")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

type LoadExportProps = {
  handleLoadExistingExport: (
    selectedExport: ExportConfig,
    table: TabMenuItems,
  ) => void;
};

export const LoadExport = ({ handleLoadExistingExport }: LoadExportProps) => {
  const { t } = useTranslation();
  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const { data: exports } = useGetExportConfigs();
  const { mutate: deleteExportConfig, isPending: deleteIsPending } =
    useDeleteExportConfig();

  const handleLoad = () => {
    if (!selectedConfigId) return;
    const config = exports?.find((e) => e.id === selectedConfigId);
    if (!config) return;
    handleLoadExistingExport(config.config, config.table);
    setOpen(false);
  };

  const handleDelete = (id: string) => {
    if (selectedConfigId === id) setSelectedConfigId(null);
    deleteExportConfig(id);
  };

  return (
    <StyledDrawer
      title={t("export.load.title")}
      description={t("export.load.description")}
      trigger={
        <Button variant="outline" className="w-fit">
          <span>{t("export.loadExistingExport")}</span>
        </Button>
      }
      sheetProps={{
        open,
        onOpenChange: setOpen,
      }}
      className="w-fit sm:max-w-[50%]"
    >
      <>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead></TableHead>
              <TableHead>{t("export.load.name")}</TableHead>
              <TableHead>{t("export.load.table")}</TableHead>
              <TableHead>{t("createdAt")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {exports?.map((config: ExportConfigDto) => (
              <TableRow
                key={config.id}
                className="hover:bg-gray-100 cursor-pointer"
                onClick={() => setSelectedConfigId(config.id)}
              >
                <TableCell className="py-3">
                  <RadioGroup
                    value={selectedConfigId ?? undefined}
                    onValueChange={(value) => setSelectedConfigId(value)}
                  >
                    <RadioGroupItem
                      value={config.id}
                      className="w-4 h-4"
                      checked={selectedConfigId === config.id}
                    />
                  </RadioGroup>
                </TableCell>
                <TableCell className="py-3 font-medium">
                  {config.name}
                </TableCell>
                <TableCell className="py-3">
                  {t(tabMenuKeyToTranslationKey(config.table))}
                </TableCell>
                <TableCell className="py-3 text-muted-foreground">
                  {new Date(config.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell className="py-3">
                  <div className="flex justify-end">
                    <button
                      aria-label={t("delete")}
                      className="p-1 rounded hover:bg-gray-100"
                      onClick={() => handleDelete(config.id)}
                      disabled={deleteIsPending}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div
          className="absolute bottom-0 left-0 right-0 flex border-t bg-white border-gray-300 p-4 gap-2"
          style={{
            boxShadow: "0 -4px 8px -2px rgba(16, 24, 40, 0.10)",
          }}
        >
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => setOpen(false)}
          >
            <span className="font-semibold">{t("cancel")}</span>
          </Button>
          <Button
            className="flex-1"
            onClick={handleLoad}
            disabled={!selectedConfigId}
          >
            <span className="font-semibold">{t("export.load")}</span>
          </Button>
        </div>
      </>
    </StyledDrawer>
  );
};
